(() => {
  if (customElements.get('oto-chair-carousel')) return;
  class OTOChairCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // A registered custom element can connect before its children are parsed.
      if (!this.querySelector('[data-status]')) {
        if (!this.markupObserver) {
          this.markupObserver = new MutationObserver(() => {
            if (this.querySelector('[data-status]')) {
              this.markupObserver.disconnect();
              this.markupObserver = null;
              this.connectedCallback();
            }
          });
          this.markupObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this.small = matchMedia('(max-width: 700px)');
      this.fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.slides = Array.from(this.querySelectorAll('.swiper-slide'));
      this.panels = Array.from(this.querySelectorAll('[data-content]'));
      this.buttons = Array.from(this.querySelectorAll('[data-select]'));
      this.buttons.forEach(button => button.addEventListener('click', () => this.select(Number(button.dataset.select)), { signal }));
      this.querySelector('.occ-prev')?.addEventListener('click', () => { this.moveDirection = -1; }, { signal, capture: true });
      this.querySelector('.occ-next')?.addEventListener('click', () => { this.moveDirection = 1; }, { signal, capture: true });
      this.addEventListener('keydown', event => {
        if (event.target.matches('input,textarea,select')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.moveDirection = event.key === 'ArrowRight' ? 1 : -1;
          if (this.swiper) this.swiper[event.key === 'ArrowRight' ? 'slideNext' : 'slidePrev']();
        }
      }, { signal });
      this.addEventListener('pointermove', event => {
        if (this.dataset.parallax !== 'true' || this.reduce.matches || this.small.matches || !this.fine.matches) return;
        const layer = this.querySelector('.swiper-slide-active .occ-parallax');
        const box = this.getBoundingClientRect();
        if (layer) layer.style.transform = `translate(${(event.clientX - box.left - box.width / 2) / box.width * 16}px,${(event.clientY - box.top - box.height / 2) / box.height * 12}px)`;
      }, { signal });
      this.addEventListener('pointerleave', () => this.resetParallax(), { signal });
      this.small.addEventListener('change', () => this.resetParallax(), { signal });
      this.reduce.addEventListener('change', () => {
        if (this.swiper) this.swiper.params.speed = this.reduce.matches ? 0 : Number(this.dataset.speed);
        if (this.reduce.matches && this.lastIndex !== undefined) {
          this.transitionCopy(this.lastIndex, false);
          this.transitionBackdrop(this.slides[this.lastIndex], false);
        }
        this.resetParallax();
      }, { signal });
      this.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) this.select(index, 0);
      }, { signal });
      this.initialize(signal);
    }

    async initialize(signal) {
      if (!this.slides.length) return;
      try {
        const { default: Swiper } = await import(new URL(this.dataset.library, document.baseURI).href);
        if (signal.aborted || !this.isConnected) return;
        const angle = Number(this.dataset.angle);
        this.swiper = new Swiper(this.querySelector('.occ-swiper'), {
          slidesPerView: 1,
          loop: this.slides.length > 1,
          speed: this.reduce.matches ? 0 : Number(this.dataset.speed),
          grabCursor: this.slides.length > 1,
          allowTouchMove: this.slides.length > 1,
          effect: 'creative',
          creativeEffect: {
            perspective: false,
            prev: { translate: ['-105%', '20%', 0], rotate: [0, 0, -angle], opacity: 0, origin: 'center bottom' },
            next: { translate: ['105%', '20%', 0], rotate: [0, 0, angle], opacity: 0, origin: 'center bottom' }
          },
          navigation: { prevEl: this.querySelector('.occ-prev'), nextEl: this.querySelector('.occ-next') },
          on: {
            init: swiper => this.sync(swiper),
            slideChange: swiper => this.sync(swiper),
            touchEnd: swiper => { if (swiper.swipeDirection) this.moveDirection = swiper.swipeDirection === 'next' ? 1 : -1; }
          }
        });
        if (this.pendingIndex !== undefined) this.select(this.pendingIndex, 0);
      } catch (error) {
        if (signal.aborted) return;
        this.buttons.forEach(button => { button.disabled = true; });
        this.querySelectorAll('.occ-arrow').forEach(button => { button.disabled = true; });
        console.warn('OTO chair carousel could not load its animation library.', error);
      }
    }

    select(index, speed) {
      if (!this.swiper) { this.pendingIndex = index; return; }
      this.moveDirection = index >= this.swiper.realIndex ? 1 : -1;
      if (this.slides.length > 1) this.swiper.slideToLoop(index, speed);
      else this.swiper.slideTo(index, speed);
    }

    sync(swiper) {
      const index = swiper.realIndex;
      const slide = this.slides[index];
      if (!slide) return;
      this.transitionCopy(index, this.lastIndex !== undefined && this.lastIndex !== index && !this.reduce.matches && swiper.params.speed > 0);
      this.buttons.forEach((button, n) => button.setAttribute('aria-pressed', String(n === index)));
      this.slides.forEach((item, n) => item.setAttribute('aria-hidden', String(n !== index)));
      this.style.setProperty('--occ-bg', slide.dataset.background);
      this.querySelectorAll('[data-occ-scene]').forEach(scene => {
        const active = Number(scene.dataset.occScene) === index;
        scene.classList.toggle('is-active', active);
        if (active) {
          this.style.setProperty('--occ-scene-text', scene.dataset.sceneText);
          this.style.setProperty('--occ-scene-footer', scene.dataset.sceneFooter);
        }
      });
      if (this.lastIndex !== index) {
        this.transitionBackdrop(slide, this.lastIndex !== undefined && !this.reduce.matches && swiper.params.speed > 0);
      }
      this.querySelector('.occ-swiper').style.mixBlendMode = slide.querySelector('.occ-blend') ? 'multiply' : 'normal';
      const count = this.querySelector('[data-current]');
      if (count) count.textContent = String(index + 1).padStart(2, '0');
      if (this.lastIndex !== undefined && this.lastIndex !== index) {
        this.querySelector('[data-status]').textContent = `${slide.dataset.title}, ${index + 1} / ${this.slides.length}`;
      }
      this.lastIndex = index;
      this.resetParallax();
    }

    async transitionBackdrop(slide, animate) {
      const viewport = this.querySelector('.occ-backdrop');
      let element = viewport.querySelector('.occ-backdrop-text');
      if (!element) {
        element = document.createElement('span');
        element.className = 'occ-backdrop-text';
        element.textContent = viewport.textContent;
        viewport.replaceChildren(element);
      }
      const opacity = Number(getComputedStyle(element).opacity);
      const transform = getComputedStyle(element).transform;
      this.backdropAnimation?.cancel();
      this.backdropAnimation = null;
      const token = {};
      this.backdropToken = token;
      const update = () => {
        element.textContent = slide.dataset.title;
        this.style.setProperty('--occ-backdrop', slide.dataset.backdrop);
      };
      if (!animate) { update(); return; }
      const duration = Math.min(500, Math.max(400, Number(this.dataset.speed) * 0.52));
      const offset = (this.moveDirection || 1) * (this.small.matches ? 16 : 22);
      try {
        const fadeOut = element.animate([{ opacity, transform }, { opacity: 0, transform: `translateY(${-offset}px)` }], {
          duration: duration * 0.4, easing: 'ease-out', fill: 'forwards'
        });
        this.backdropAnimation = fadeOut;
        await fadeOut.finished;
        if (this.backdropToken !== token || !this.isConnected) return;
        update();
        const fadeIn = element.animate([{ opacity: 0, transform: `translateY(${offset}px)` }, { opacity: 1, transform: 'translateY(0)' }], {
          duration: duration * 0.6, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'forwards'
        });
        this.backdropAnimation = fadeIn;
        fadeOut.cancel();
        await fadeIn.finished;
        if (this.backdropToken !== token) return;
        fadeIn.cancel();
        this.backdropAnimation = null;
      } catch (_) { /* A newer selection or disconnection cancelled this fade. */ }
    }

    transitionCopy(index, animate) {
      this.copyAnimations ||= new Map();
      const duration = Math.min(380, Math.max(180, Number(this.dataset.speed) * 0.4));
      this.panels.forEach((panel, n) => {
        const active = n === index;
        const opacity = panel.hidden ? 0 : Number(getComputedStyle(panel).opacity);
        this.copyAnimations.get(panel)?.cancel();
        this.copyAnimations.delete(panel);
        panel.inert = !active;
        panel.setAttribute('aria-hidden', String(!active));
        if (!animate || (!active && panel.hidden)) {
          panel.hidden = !active;
          return;
        }
        panel.hidden = false;
        const animation = panel.animate([{ opacity }, { opacity: active ? 1 : 0 }], {
          duration, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'forwards'
        });
        this.copyAnimations.set(panel, animation);
        animation.finished.then(() => {
          if (this.copyAnimations.get(panel) !== animation) return;
          panel.hidden = !active;
          animation.cancel();
          this.copyAnimations.delete(panel);
        }).catch(() => {});
      });
    }

    resetParallax() { this.querySelectorAll('.occ-parallax').forEach(layer => { layer.style.transform = ''; }); }

    disconnectedCallback() {
      this.markupObserver?.disconnect();
      this.markupObserver = null;
      this.controller?.abort();
      this.backdropToken = null;
      this.backdropAnimation?.cancel();
      this.backdropAnimation = null;
      this.lastIndex = undefined;
      this.copyAnimations?.forEach(animation => animation.cancel());
      this.copyAnimations?.clear();
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.controller = null;
      this.pendingIndex = undefined;
    }
  }
  customElements.define('oto-chair-carousel', OTOChairCarousel);
})();
